import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import moment from 'moment'
import API from '../../../services/API'
import DashboardCards from './DashboardCards'
import SearchBar, { FORM_TYPES } from './SearchBar'
import MFALogin from '../../../helpers/auth/MFALogin'
import Loading from '../../loading/loadingPage'
import CustomDataGrid from '../../../components/DataGrid/CustomDataGrid'
import Age from '../../../components/Age'
import AccessLogsBoard from './accessLogs/AccessLogsBoard'

import Alert from '@mui/material/Alert'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'

import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelIcon from '@mui/icons-material/Cancel'
import DocumentScannerIcon from '@mui/icons-material/DocumentScanner'
import HistoryIcon from '@mui/icons-material/History'
import ListAltIcon from '@mui/icons-material/ListAlt'
import LocalHospitalIcon from '@mui/icons-material/LocalHospital'

function useQuery() {
  const { search } = useLocation()
  return React.useMemo(() => new URLSearchParams(search), [search])
}

const isEpcrType = (recordType) =>
  recordType === 'ePCR' ||
  recordType === 'Fast Track' ||
  recordType === 'Welfare' ||
  recordType === 'Critical Care'

const CentredCell = ({ children }) => (
  <Box
    display="flex"
    height="100%"
    width="100%"
    alignItems="center"
    justifyContent="center"
  >
    {children}
  </Box>
)

const Dashboard = ({}) => {
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState({})
  const [fetchError, setFetchError] = useState(false)
  const [requiresMFA, setRequiresMFA] = useState(false)
  const [searchParams, setSearchParams] = useState({
    epcrNumber: null,
    startDate: moment().subtract(1, 'week'),
    endDate: moment(),
    status: ['Draft', 'Submitted'],
    event: null,
    firstName: null,
    lastName: null,
    dob: null,
    types: [...FORM_TYPES],
  })
  const [searchResults, setSearchResults] = useState([])
  const [rowCount, setRowCount] = useState(0)
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  })
  const [loadingSearch, setLoadingSearch] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  // Preset handed to the Access log tab by a dashboard tile; a fresh object
  // per click so the board reloads even for the same preset.
  const [accessLogFilters, setAccessLogFilters] = useState(null)

  const loadDashboard = () => {
    API.get('/governance/epcrDashboard')
      .then((res) => {
        if (res.status === 200) {
          setData(res.data)
          setRequiresMFA(false)
        } else if (res.status === 403) {
          setRequiresMFA(true)
        } else {
          setFetchError(true)
        }
      })
      .finally(() => setLoading(false))
  }

  // Changing any filter starts back at page 0; the grid's pagination controls
  // pass their own model through `pagination`.
  const performSearch = (
    customParams = {},
    pagination = { page: 0, pageSize: paginationModel.pageSize },
  ) => {
    setLoadingSearch(true)
    setPaginationModel(pagination)
    API.post('/governance/epcr/search', {
      ...searchParams,
      ...customParams,
      completedBy: searchParams?.completedBy?.userAccount?._id,
      page: pagination.page,
      pageSize: pagination.pageSize,
    })
      .then((res) => {
        if (res.status === 200) {
          setSearchResults(res.data.records)
          setRowCount(res.data.total)
        }
      })
      .finally(() => setLoadingSearch(false))
  }

  let query = useQuery()
  const defaultview = query.get('defaultview')
  // ?view=access-logs shows the all-records access log in place of the
  // record search, so the tab can be linked to directly.
  const view = query.get('view') === 'access-logs' ? 'access-logs' : 'records'

  const switchView = (next) => {
    const params = new URLSearchParams(location.search)
    if (next === 'access-logs') params.set('view', 'access-logs')
    else params.delete('view')
    navigate(
      { pathname: location.pathname, search: params.toString() },
      { replace: true },
    )
  }

  const openAccessLogs = (presetFilters) => {
    setAccessLogFilters({ ...(presetFilters ?? {}) })
    switchView('access-logs')
  }

  useEffect(() => {
    loadDashboard()
    if (defaultview === 'newhsincidents') {
      performSearch({
        hsConcern: true,
        status: ['Draft', 'Submitted'],
      })
      setSearchParams((prev) => ({
        ...prev,
        hsConcern: true,
        status: ['Draft', 'Submitted'],
      }))
    } else if (defaultview === 'newsafeguardingconcerns') {
      performSearch({
        sgConcerns: 'New',
        status: ['Draft', 'Submitted'],
      })
      setSearchParams((prev) => ({
        ...prev,
        sgConcerns: 'New',
        status: ['Draft', 'Submitted'],
      }))
    } else {
      performSearch()
    }
  }, [requiresMFA])

  return loading ? (
    <Loading />
  ) : (
    <Stack gap={2}>
      <Stack
        gap={1}
        direction={{ xs: 'column', lg: 'row' }}
        justifyContent="space-between"
        alignItems={{ lg: 'center' }}
      >
        <Box>
          <Typography variant="h6">
            {view === 'access-logs'
              ? 'Record Access Log'
              : 'Patient Care Records'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {view === 'access-logs'
              ? 'Who has viewed, downloaded, shared or updated patient care records'
              : `${
                  searchParams?.startDate && searchParams?.endDate
                    ? `${moment(searchParams.startDate).format(
                        'ddd DD MMM YYYY',
                      )} – ${moment(searchParams.endDate).format(
                        'ddd DD MMM YYYY',
                      )}`
                    : 'All dates'
                } · ${rowCount} record${rowCount === 1 ? '' : 's'}`}
          </Typography>
        </Box>
        {!requiresMFA && view === 'records' && (
          <Button
            startIcon={<DocumentScannerIcon />}
            onClick={() => navigate('/patient-report-forms/upload-paper-prf')}
            variant="contained"
            color="anMainBlue"
          >
            Upload Paper PRF
          </Button>
        )}
      </Stack>

      {requiresMFA && <MFALogin onSuccess={loadDashboard} />}

      {!requiresMFA && (
        <Tabs
          value={view}
          onChange={(event, next) => switchView(next)}
          sx={{
            minHeight: 40,
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              minHeight: 40,
              textTransform: 'none',
              fontWeight: 700,
            },
          }}
        >
          <Tab
            value="records"
            label="Records"
            icon={<ListAltIcon fontSize="small" />}
            iconPosition="start"
          />
          <Tab
            value="access-logs"
            label="Access log"
            icon={<HistoryIcon fontSize="small" />}
            iconPosition="start"
          />
        </Tabs>
      )}

      {!requiresMFA && view === 'access-logs' && (
        <AccessLogsBoard initialFilters={accessLogFilters} />
      )}

      {!requiresMFA && view === 'records' && (
        <>
          {fetchError && (
            <Alert severity="error">
              Failed to load the dashboard. Please try again.
            </Alert>
          )}
          {defaultview === 'newhsincidents' && (
            <Alert severity="info">
              Default view: Incidents with new Health and Safety Concerns
            </Alert>
          )}
          {defaultview === 'newsafeguardingconcerns' && (
            <Alert severity="info">Default view: New Safeguarding Incidents</Alert>
          )}

          <DashboardCards
            loading={loading}
            data={data}
            setSearchParams={setSearchParams}
            performSearch={performSearch}
            onOpenAccessLogs={openAccessLogs}
          />

          <SearchBar
            searchParams={searchParams}
            setSearchParams={setSearchParams}
            performSearch={performSearch}
            loadingSearch={loadingSearch}
          />

          <CustomDataGrid
            rows={searchResults}
            getRowId={(row) => row._id}
            loading={loadingSearch}
            maxHeight={600}
            rowHeight={58}
            hideFilters
            paginationMode="server"
            rowCount={rowCount}
            paginationModel={paginationModel}
            onPaginationModelChange={(model) => performSearch({}, model)}
            pageSizeOptions={[25, 50, 100]}
            disableColumnSorting
            onRowClick={({ row }) =>
              row.recordType === 'Paper'
                ? window.open(
                    '/patient-report-forms/paper/' + row._id,
                    '_blank',
                    'noopener,noreferrer',
                  )
                : window.open(
                    '/patient-report-forms/' + row._id,
                    '_blank',
                    'noopener,noreferrer',
                  )
            }
            disableRowSelectionOnClick
            initialState={{
              columns: {
                columnVisibilityModel: {
                  date: true,
                  patient: true,
                  reportedComplaint: false,
                  presentingComplaint: false,
                  incidentType: false,
                  occurredAtEvent: false,
                  hsReviewed: defaultview === 'newhsincidents',
                  ptOutcome: false,
                  completedBy: true,
                  currentOwner: true,
                  status: true,
                  recordType: false,
                  event: true,
                },
              },
            }}
            sx={{
              '.MuiDataGrid-cell:focus': {
                outline: 'none',
              },
              '& .MuiDataGrid-row:hover': {
                cursor: 'pointer',
              },
            }}
            columns={[
              {
                field: 'epcrNumber',
                headerName: 'ePCR No',
                minWidth: 100,
                renderCell: ({ row }) =>
                  row?.epcrNumber ? (
                    <Typography variant="body2" fontWeight={600}>
                      {row.epcrNumber}
                    </Typography>
                  ) : (
                    ''
                  ),
              },
              {
                field: 'date',
                headerName: 'Date',
                minWidth: 170,
                valueGetter: (value, row) =>
                  moment(
                    row?.incidentDate ??
                      row?.incident?.incidentDate ??
                      row.createdAt,
                  ).unix(),
                renderCell: ({ row }) => {
                  const date = moment(
                    row?.incidentDate ??
                      row?.incident?.incidentDate ??
                      row.createdAt,
                  )
                  return (
                    <Stack sx={{ overflow: 'hidden' }}>
                      <Typography variant="body2" noWrap>
                        {date.format('ddd DD MMM YYYY')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {date.format('HH:mm')}
                      </Typography>
                    </Stack>
                  )
                },
              },
              {
                field: 'status',
                headerName: 'Status',
                align: 'center',
                headerAlign: 'center',
                minWidth: 120,
                valueGetter: (value, row) =>
                  isEpcrType(row.recordType)
                    ? row?.review?.status
                    : 'Submitted',
                renderCell: ({ row }) => {
                  const status = isEpcrType(row.recordType)
                    ? row?.review?.status
                    : 'Submitted'
                  return (
                    <Chip
                      size="small"
                      label={status}
                      color={
                        status === 'Draft'
                          ? 'error'
                          : status === 'Submitted'
                            ? 'success'
                            : 'info'
                      }
                      sx={{ fontWeight: 700, borderRadius: 1, width: '100%' }}
                    />
                  )
                },
              },
              {
                field: 'eventTitle',
                headerName: 'Event',
                valueGetter: (value, row) =>
                  row?.incident?.linkedEvent
                    ? row?.incident?.linkedEvent.title
                    : row?.event?.title,
                flex: 1,
                minWidth: 200,
              },
              {
                field: 'recordType',
                headerName: 'Record Type',
                minWidth: 130,
                renderCell: ({ row }) => (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={row.recordType}
                    sx={{ borderRadius: 1 }}
                  />
                ),
              },
              {
                field: 'patient',
                headerName: 'Patient',
                minWidth: 200,
                flex: 1,
                valueGetter: (value, row) =>
                  row?.competitorNumber
                    ? row.competitorNumber
                    : `${row?.demographics?.lastName ?? ''} ${
                        row?.demographics?.firstName ?? ''
                      }`.trim(),
                renderCell: ({ row }) => {
                  if (row?.competitorNumber)
                    return (
                      <Typography variant="body2">
                        {row.competitorNumber}
                      </Typography>
                    )
                  const lastName = row?.demographics?.lastName
                  const firstName = row?.demographics?.firstName
                  const name = `${lastName ?? ''}${
                    lastName && firstName ? ', ' : ''
                  }${firstName ? firstName[0] : ''}`
                  return (
                    <Stack sx={{ overflow: 'hidden' }}>
                      <Typography variant="body2" noWrap>
                        {name || '—'}
                      </Typography>
                      {row?.demographics?.dob && (
                        <Typography variant="caption" color="text.secondary">
                          {Age(row.demographics.dob, row?.createdAt)}
                        </Typography>
                      )}
                    </Stack>
                  )
                },
              },
              {
                field: 'reportedComplaint',
                headerName: 'Reported Complaint',
                valueGetter: (value, row) =>
                  row?.type
                    ? row.type
                    : (row?.incident?.reportedComplaint ??
                      row?.welfare?.category ??
                      ''),
                minWidth: 200,
                flex: 1,
              },
              {
                field: 'presentingComplaint',
                headerName: 'Presenting Complaint',
                valueGetter: (value, row) => row?.history?.presentingComplaint,
                minWidth: 240,
                flex: 1,
              },
              {
                field: 'incidentType',
                headerName: 'Incident Type',
                valueGetter: (value, row) => row?.incident?.incidentType ?? '',
                minWidth: 150,
              },
              {
                field: 'occurredAtEvent',
                headerName: 'Occurred at Event',
                valueGetter: (value, row) =>
                  row?.incident?.incidentType === 'Event'
                    ? (row?.incident?.injuryOccurredAtEvent ?? '')
                    : '',
                minWidth: 150,
              },
              {
                field: 'hsReviewed',
                headerName: 'H&S Reviewed',
                valueGetter: (value, row) =>
                  !!row?.incident?.healthSafetyConcernManagerReview,
                renderCell: ({ row }) => (
                  <CentredCell>
                    {row?.incident?.healthSafetyConcernManagerReview ? (
                      <CheckCircleIcon color="success" />
                    ) : (
                      <CancelIcon color="error" />
                    )}
                  </CentredCell>
                ),
              },
              {
                field: 'ptOutcome',
                headerName: 'Patient Outcome',
                valueGetter: (value, row) => row?.plan?.ptOutcome ?? '',
                minWidth: 250,
              },
              {
                field: 'completedBy',
                headerName: 'Completed By',
                headerAlign: 'center',
                align: 'center',
                minWidth: 130,
                valueGetter: (value, row) => {
                  const person = isEpcrType(row.recordType)
                    ? row.review?.completedBy
                    : (row.completedBy?.userAccount ?? row.completedBy)
                  return (
                    `${person?.firstName ?? ''} ${person?.lastName ?? ''}`.trim() ||
                    // Frozen snapshot: survives the author's account lapsing
                    // (subcontractors) or being archived
                    row.review?.completedByRecord?.name ||
                    ''
                  )
                },
                renderCell: ({ row }) => {
                  const person = isEpcrType(row.recordType)
                    ? row.review?.completedBy
                    : (row.completedBy?.userAccount ?? row.completedBy)
                  const name =
                    `${person?.firstName ?? ''} ${person?.lastName ?? ''}`.trim() ||
                    row.review?.completedByRecord?.name
                  if (!name) return ''
                  return (
                    <CentredCell>
                      <Tooltip title={name}>
                        <Avatar
                          sx={{ width: 28, height: 28, borderRadius: 1 }}
                          src={person?.photograph}
                        />
                      </Tooltip>
                    </CentredCell>
                  )
                },
              },
              {
                field: 'currentOwner',
                headerName: 'Current Owner',
                headerAlign: 'center',
                align: 'center',
                minWidth: 130,
                valueGetter: (value, row) => {
                  if (
                    isEpcrType(row.recordType) &&
                    row.review?.currentTreatmentLocation
                  ) {
                    return row.review.currentTreatmentLocation.name
                  }
                  const person = isEpcrType(row.recordType)
                    ? (row.review?.currentOwner ?? row.review?.completedBy)
                    : (row.completedBy?.userAccount ?? row.completedBy)
                  return person
                    ? `${person.firstName ?? ''} ${person.lastName ?? ''}`.trim()
                    : ''
                },
                renderCell: ({ row }) => {
                  if (
                    isEpcrType(row.recordType) &&
                    row.review?.currentTreatmentLocation
                  ) {
                    return (
                      <CentredCell>
                        <Tooltip
                          title={row.review.currentTreatmentLocation.name}
                        >
                          <Avatar
                            sx={{
                              width: 28,
                              height: 28,
                              borderRadius: 1,
                              bgcolor: 'primary.main',
                            }}
                          >
                            <LocalHospitalIcon fontSize="small" />
                          </Avatar>
                        </Tooltip>
                      </CentredCell>
                    )
                  }
                  const person = isEpcrType(row.recordType)
                    ? (row.review?.currentOwner ?? row.review?.completedBy)
                    : null
                  if (!person) return ''
                  return (
                    <CentredCell>
                      <Tooltip
                        title={`${person.firstName ?? ''} ${
                          person.lastName ?? ''
                        }`.trim()}
                      >
                        <Avatar
                          sx={{ width: 28, height: 28, borderRadius: 1 }}
                          src={person.photograph}
                        />
                      </Tooltip>
                    </CentredCell>
                  )
                },
              },
            ]}
          />
        </>
      )}
    </Stack>
  )
}

export default Dashboard
